import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ShareOption } from '../components/common/SharePopup';

interface UseShareOptions {
  url: string;
  title?: string;
  text?: string;
}

export const useShare = ({ url, title, text }: UseShareOptions) => {
  const { t } = useTranslation();

  const getDefaultShareOptions = useCallback((): ShareOption[] => [
    {
      id: 'whatsapp',
      label: t('common.shareVia.whatsapp'),
      icon: 'ri:whatsapp-fill',
      iconColor: 'bg-[#25D366] text-white',
      onClick: () => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(url)}`;
        window.open(whatsappUrl, '_blank');
      },
    },
    {
      id: 'telegram',
      label: t('common.shareVia.telegram'),
      icon: 'ri:telegram-fill',
      iconColor: 'bg-[#0088cc] text-white',
      onClick: () => {
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
        window.open(telegramUrl, '_blank');
      },
    },
    {
      id: 'facebook',
      label: t('common.shareVia.facebook'),
      icon: 'ri:facebook-circle-fill',
      iconColor: 'bg-[#1877f2] text-white',
      onClick: () => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        window.open(facebookUrl, '_blank');
      },
    },
    {
      id: 'email',
      label: t('common.shareVia.email'),
      icon: 'ri:mail-fill',
      iconColor: 'bg-[#EA4335] text-white',
      onClick: () => {
        const subject = title ? encodeURIComponent(title) : '';
        const body = text ? encodeURIComponent(text + '\n\n' + url) : encodeURIComponent(url);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      },
    },
  ], [t, url, title, text]);

  const isShareSupported = typeof navigator !== 'undefined' && 'share' in navigator;

  const handleQuickShare = useCallback(async () => {
    if (!isShareSupported) return;

    try {
      await navigator.share({
        title,
        text,
        url,
      });
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
        // TODO: Show error toast or tooltip
      }
    }
  }, [isShareSupported, title, text, url]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      // TODO: Show success toast or tooltip
    } catch (error) {
      console.error('Failed to copy link:', error);
      // TODO: Show error toast or tooltip
    }
  }, [url]);

  return {
    shareOptions: getDefaultShareOptions(),
    isShareSupported,
    handleQuickShare,
    handleCopyLink,
  };
};

export default useShare; 